import { Box, Collapse, Stack, Typography, useTheme } from '@mui/material';
import {
  CounterAbsoluteStyled,
  CounterStyled,
  DividerStyled,
  DrawerHeaderStyled,
  SideBarStyled,
  SliderItem,
} from './side-bar.styled';
import { IconSize, WebSocketEvents } from '@constants';
import React, { useCallback, useEffect, useState } from 'react';
import routes, { dividerPosition } from '@/routes';
import { useLocation, useNavigate } from 'react-router-dom';

import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { JButton } from '@atoms/JButton';
import Jchip from '@atoms/JChip';
import Logo from '@atoms/Logo';
import { NOTIFICATION_EVENTS } from '@services/endpoints';
import Paths from '@/routes/paths';
import useAuth from '@authentication/hooks/useAuth';
import useGetCvVideosList from '@cv-videos/hooks/useGetCvVideosList';
import useJDialog from '@/hooks/useJDialog';
import useLogout from '@authentication/hooks/useLogout';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useWebSocket from '@/hooks/useWebSocket';

interface LogoutDialogBodyProps {
  onClose: () => void;
  onConfirm: () => void;
}

const SideBar = () => {
  const [open, setOpen] = useState(true);

  const theme = useTheme();
  const { t } = useTranslation();
  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleOnMouseOver = () => {
    setOpen(true);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showJDialog, closeJDialog } = useJDialog();

  const { cvVideosPendingCount, setCvVideosPendingCount } =
    useGetCvVideosList();
  const [unviewedNotificationCount, setUnviewedNotificationCount] = useState(0);

  const queryClient = useQueryClient();
  const logout = useLogout();

  const logoutAndClose = () => {
    logout();
    closeJDialog();
  };

  const openLogoutDialog = useCallback(() => {
    showJDialog(
      t('LOGOUT'),
      <LogoutDialogBody onClose={closeJDialog} onConfirm={logoutAndClose} />,
    );
  }, [t]);

  const { socket } = useWebSocket(NOTIFICATION_EVENTS);
  useEffect(() => {
    cvVideosPendingCount ?? setCvVideosPendingCount(0);
  }, [cvVideosPendingCount]);
  useEffect(() => {
    if (!user) return;
    if (!socket) return;

    socket.emit('joinRoom', {
      roomName: user.id,
    });

    socket.on(WebSocketEvents.onNewCvVideo, (data) => {
      queryClient.invalidateQueries(['cv-videos']);
      queryClient.invalidateQueries(['notifications']);
      setUnviewedNotificationCount(data.unviewedNotificationCount);
      setCvVideosPendingCount(data.cvVideoPendingCount);
    });
  }, [socket, user, navigate]);

  useEffect(() => {
    if (!socket) return;
    socket.emit(WebSocketEvents.newCvVideo);
  }, [socket]);

  const displayCounter = useCallback(
    (withCounter: boolean, type: string, isInline?: boolean): boolean => {
      const count: number =
        (type === Paths.CvVideos
          ? cvVideosPendingCount
          : unviewedNotificationCount) ?? 0;
      const isInlineCounter = isInline ? open : !open;
      return !!(withCounter && isInlineCounter) && count > 0;
    },
    [cvVideosPendingCount, unviewedNotificationCount, open],
  );

  return (
    <SideBarStyled
      variant="permanent"
      open={open}
      onMouseOver={handleOnMouseOver}
      onMouseLeave={handleDrawerClose}
    >
      <DrawerHeaderStyled open={open}>
        <Collapse orientation="horizontal" in={open}>
          <Logo />
        </Collapse>
        <Jchip size="small" label="ADMIN" color="primary" key="sidebare" />
      </DrawerHeaderStyled>

      <Stack gap={2} mt={10} flexGrow={1} pb={2}>
        {routes
          .filter((item) => item.displayOnSideBar)
          .map((route) => (
            <>
              {route.divider === dividerPosition.top && <DividerStyled />}
              <SliderItem
                data-qa={route.label}
                direction="row"
                alignItems="center"
                open={open}
                selected={location.pathname === route.path}
                key={route.label}
                onClick={() => navigate(route.path)}
              >
                {route.icon &&
                  React.cloneElement(route.icon, {
                    height: IconSize.md,
                    width: IconSize.md,
                    color: theme.palette.white.light,
                  })}
                {displayCounter(!!route.withCounter, route.path) ? (
                  <CounterAbsoluteStyled>
                    {route.path === Paths.CvVideos
                      ? cvVideosPendingCount
                      : unviewedNotificationCount}
                  </CounterAbsoluteStyled>
                ) : (
                  <></>
                )}
                <Collapse orientation="horizontal" in={open}>
                  <Stack
                    direction={'row'}
                    gap={2}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Box ml={2}>{t(route?.label ?? '')}</Box>
                    {displayCounter(!!route.withCounter, route.path, true) ? (
                      <CounterStyled>
                        {route.path === Paths.CvVideos
                          ? cvVideosPendingCount
                          : unviewedNotificationCount}
                      </CounterStyled>
                    ) : (
                      <></>
                    )}
                  </Stack>
                </Collapse>
              </SliderItem>
              {route.divider === dividerPosition.bottom && <DividerStyled />}
            </>
          ))}

        <Box flexGrow={1} />
        <SliderItem
          data-qa={'logout'}
          direction="row"
          alignItems="center"
          open={open}
          selected={false}
          onClick={openLogoutDialog}
        >
          <ArrowLeftIcon
            height={IconSize.md}
            color={theme.palette.white.light}
          />

          <Collapse orientation="horizontal" in={open}>
            <Stack
              direction={'row'}
              gap={2}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Box ml={2}>{t('LOGOUT')}</Box>
            </Stack>
          </Collapse>
        </SliderItem>
      </Stack>
    </SideBarStyled>
  );
};

const LogoutDialogBody = ({ onClose, onConfirm }: LogoutDialogBodyProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography>Êtes-vous sûr de vouloir vous déconnecter ?</Typography>

      <Stack direction={'row'} gap={1} mt={1}>
        <JButton
          onClick={onConfirm}
          variant="contained"
          color="error"
          fullWidth
        >
          {t('LOGOUT')}
        </JButton>
        <JButton onClick={onClose} variant="contained" color="info">
          {t('CANCEL')}
        </JButton>
      </Stack>
    </>
  );
};

export default SideBar;
